import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { DEFAULT_SETTINGS, setCurrentClientId } from "@/data/constants";
import { api, errMsg } from "@/lib/api";

const withDefaults = (s = {}) => ({ ...DEFAULT_SETTINGS, ...Object.fromEntries(Object.entries(s).filter(([, v]) => v !== null && v !== undefined && !Number.isNaN(v))) });
const EMPTY = { clients: [], trips: [], conversations: [], activity: [], agents: [], announcements: [], automations: [], companions: [], groups: [], groupBookings: [], settings: {} };
const LOCKED_MSG = "Your account is locked. Please contact your manager.";
const Ctx = createContext(null);

export function DemoProvider({ children }) {
  // status: loading | anon | mustChange | ready
  const [status, setStatus] = useState("loading");
  const [data, setData] = useState(EMPTY);
  const [notice, setNotice] = useState(null);
  // Consecutive failures of the background poll. A single transient blip (network
  // hiccup or a cross-site cookie not delivered) must NOT sign the user out mid-work;
  // only a sustained auth loss (a few polls in a row) tears the session down.
  const failsRef = useRef(0);

  const refresh = useCallback(async ({ background = false } = {}) => {
    try {
      const { data: d } = await api.get("/state");
      const ids = new Set(d.clients.map((c) => c.id));
      if (d.me.kind === "agent") {
        d.trips = d.trips.filter((t) => ids.has(t.clientId));
        d.conversations = d.conversations.filter((c) => ids.has(c.clientId));
      }
      setCurrentClientId(d.me.clientId || null);
      failsRef.current = 0;
      setData(d);
      setStatus("ready");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === LOCKED_MSG) { setNotice(detail); setStatus("anon"); setData(EMPTY); return; }
      if (detail === "Password change required") { setStatus("mustChange"); setData(EMPTY); return; }
      if (detail === "Agreement signature required") { setStatus("agreement"); setData(EMPTY); return; }
      // Background poll: tolerate up to 2 failures (~30s) before giving up, so a
      // transient blip can't kick an actively-working user back to the login screen.
      if (background) {
        failsRef.current += 1;
        if (failsRef.current < 3) return;
      }
      failsRef.current = 0;
      setStatus("anon");
      setData(EMPTY);
    }
  }, []);

  useEffect(() => {
    refresh();
    if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => {});
    }
  }, [refresh]);

  useEffect(() => {
    if (status !== "ready") return undefined;
    const i = setInterval(() => refresh({ background: true }), 15000);
    return () => clearInterval(i);
  }, [status, refresh]);

  const value = useMemo(() => {
    const run = async (fn, ok) => {
      try {
        const r = await fn();
        await refresh();
        if (ok) toast.success(ok);
        return r.data;
      } catch (e) {
        toast.error(errMsg(e));
        return null;
      }
    };
    return {
      ...data,
      status,
      settings: withDefaults(data.settings),
      isAdmin: data.me?.role === "admin",
      refresh,
      notice,
      setNotice,
      clearNotice: () => setNotice(null),
      client: (id) => data.clients.find((c) => c.id === id),
      trips: data.trips.filter((t) => !t.archived),
      archivedTrips: data.trips.filter((t) => t.archived),
      trip: (id) => data.trips.find((t) => t.id === id),
      paidExtra: (tripId) => (data.authorizations || []).filter((a) => a.tripId === tripId && a.status === "COMPLETE").reduce((s, a) => s + a.amount, 0),
      agentName: (id) => data.agents.find((a) => a.id === id)?.name,
      tripsFor: (clientId) => data.trips.filter((t) => t.clientId === clientId && !t.archived),
      convFor: (clientId) => data.conversations.find((c) => c.clientId === clientId),
      send: (clientId, _from, text, tripId) => run(() => api.post("/messages", { clientId, text, tripId })),
      markRead: (convId) => run(() => api.post(`/conversations/${convId}/read`)),
      group: (id) => data.groups.find((g) => g.id === id),
      groupBookingsFor: (groupId) => (data.groupBookings || []).filter((b) => b.groupId === groupId),
      createGroup: (g) => run(() => api.post("/groups", g), "Group created"),
      updateGroup: (id, patch) => run(() => api.patch(`/groups/${id}`, patch), "Group saved"),
      addGroupBooking: (groupId, body) => run(() => api.post(`/groups/${groupId}/bookings`, body), "Booking added"),
      updateGroupBooking: (groupId, bid, status) => run(() => api.patch(`/groups/${groupId}/bookings/${bid}`, { status })),
      deleteGroupBooking: (groupId, bid) => run(() => api.delete(`/groups/${groupId}/bookings/${bid}`), "Booking removed"),
      convertBookingToQuote: (groupId, bid) => run(() => api.post(`/groups/${groupId}/bookings/${bid}/convert-to-quote`)),
      createQuote: (t) => run(() => api.post("/trips", t)),
      convert: (tripId) => run(() => api.post(`/trips/${tripId}/convert`)),
      updateTrip: (tripId, patch) => run(() => api.patch(`/trips/${tripId}`, patch)),
      uploadTripDocument: (tripId, file) => run(() => { const fd = new FormData(); fd.append("file", file); return api.post(`/trips/${tripId}/documents`, fd); }, "Document uploaded"),
      renameTripDocument: (tripId, docId, name) => run(() => api.patch(`/trips/${tripId}/documents/${docId}`, { name }), "Document renamed"),
      deleteTripDocument: (tripId, docId) => run(() => api.delete(`/trips/${tripId}/documents/${docId}`), "Document removed"),
      archiveTrip: (tripId) => run(() => api.post(`/trips/${tripId}/archive`), "Trip moved to Archived"),
      restoreTrip: (tripId) => run(() => api.post(`/trips/${tripId}/restore`), "Trip restored"),
      deleteTrip: (tripId) => run(() => api.delete(`/trips/${tripId}`), "Trip deleted permanently"),
      updateSettings: (patch) => run(() => api.patch("/settings", patch)),
      updateClient: (clientId, patch) => run(() => api.patch(`/clients/${clientId}`, patch)),
      createClient: (c) => run(() => api.post("/clients", c), "Client added"),
      createAnnouncement: (a) => run(() => api.post("/announcements", a), "Announcement posted"),
      deleteAnnouncement: (id) => run(() => api.delete(`/announcements/${id}`), "Announcement removed"),
      addHouseholdMember: (id, payload) => run(() => api.post(`/clients/${id}/household/members`, payload), "Household member added"),
      makeHouseholdPrimary: (id) => run(() => api.post(`/clients/${id}/household/primary`), "Primary member updated"),
      removeHouseholdMember: (id) => run(() => api.post(`/clients/${id}/household/remove`), "Member removed from household"),
      inviteClient: (id) => run(() => api.post(`/clients/${id}/invite`)),
      addLoyalty: (id, payload) => run(() => api.post(`/clients/${id}/loyalty`, payload), "Loyalty account added"),
      removeLoyalty: (id, lid) => run(() => api.delete(`/clients/${id}/loyalty/${lid}`), "Loyalty account removed"),
      reassignClient: (id, agentId) => run(() => api.post(`/clients/${id}/reassign`, { agentId }), "Client reassigned"),
      resetClientPassword: (id) => run(() => api.post(`/clients/${id}/reset-portal-password`)),
      deleteClient: (id) => run(() => api.delete(`/clients/${id}`), "Account deleted"),
      createAgent: (a) => run(() => api.post("/agents", a)),
      patchAgent: (id, patch) => run(() => api.patch(`/agents/${id}`, patch)),
      deleteAgent: (id) => run(() => api.delete(`/agents/${id}`), "Agent removed"),
      changeClientPassword: (body) => run(() => api.post("/auth/client-change-password", body), "Password updated"),
      requestCard: (body) => run(() => api.post("/cards/request", body), "Request sent to your advisor"),
      addCard: (clientId, brand, last4) => run(() => api.post("/cards", { clientId, brand, last4 }), "Card added"),
      removeCard: (cardId) => run(() => api.delete(`/cards/${cardId}`), "Card removed"),
      resolveCardRequest: (id) => run(() => api.post(`/card-requests/${id}/resolve`), "Request dismissed"),
      approveCardRequest: (id) => run(() => api.post(`/card-requests/${id}/approve`), "Card approved"),
      addManualPayment: (tripId, body) => run(() => api.post(`/trips/${tripId}/payment`, body), "Payment added"),
      markWelcomeSeen: async () => { try { await api.post("/client/welcome-seen"); } catch { /* non-blocking */ } setData((d) => ({ ...d, me: { ...d.me, welcomeSeen: true } })); },
      notifications: data.notifications || [],
      markNotificationsRead: () => run(() => api.post("/notifications/read")),
      submitFeedback: (body) => run(() => api.post("/feedback", body), "Thanks! Your feedback was sent to the team"),
      authorizePayment: (tripId, amount, cardId) => run(() => api.post(`/trips/${tripId}/authorize`, { amount, cardId }), "Payment authorized — your advisor has been notified"),
      completeAuthorization: (id) => run(() => api.post(`/authorizations/${id}/complete`), "Payment marked complete"),
      tripInviteLink: async (tripId) => { const { data } = await api.post(`/trips/${tripId}/invite-link`); return data.link; },
      updateAutomation: (patch) => run(() => api.patch("/automations", patch), "Automation saved"),
      logout: async () => {
        await api.post("/auth/logout").catch(() => null);
        setCurrentClientId(null);
        setData(EMPTY);
        setStatus("anon");
      },
    };
  }, [data, status, refresh, notice]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useDemo = () => useContext(Ctx);
